/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Independent per-scope version history (invalid:false marks the current row).
 */

import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import { Tenant, TemplateVersion, Scope } from './types';
import { scopeLabel, forkedFromLabel } from './model';

export interface TradeVersionHistoryProps {
  trade: { name: string; provider: unknown } | null;
  scope: Scope; tenantId: string | null; tenants: Tenant[];
  versions: TemplateVersion[];
  activeSelectedVersion: string;
  onMakeActive: (version: string) => void;
  onEditVersion: (version: string) => void;
}

/** Independent per-scope version history. `status` stays 'active' on every row — `invalid:false` marks the current one. */
export default function TradeVersionHistory({ trade, scope, tenantId, tenants, versions, activeSelectedVersion, onMakeActive, onEditVersion }: TradeVersionHistoryProps) {
  return (
    <Box sx={{ p: 3, maxWidth: 760, mx: 'auto' }}>
      <Typography variant="h6" fontWeight={700}>{trade?.name} — {scopeLabel(scope, tenantId, tenants)}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Independent version history for this scope. Numbering does not sync with other scopes.
      </Typography>
      {scope === 'tenant' && trade && !trade.provider && (
        <Box sx={{ bgcolor: theme => alpha(theme.palette.warning.main, 0.1), border: 1, borderColor: 'warning.main', borderRadius: 1, p: 1.5, mb: 2, fontSize: 12 }}>
          This trade has no dedicated provider template yet — this tenant's versions started from the Global Default. Editing a version offers a chance to promote it.
        </Box>
      )}
      {versions.slice().reverse().map(v => {
        const isViewing = v.version === activeSelectedVersion;
        const isCurrent = v.invalid === false;
        return (
          <Card key={v.version} variant="outlined" sx={{ p: 1.75, mb: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1.5, borderWidth: isViewing ? 2 : 1, borderColor: isViewing ? 'primary.main' : 'divider' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: isCurrent ? 'primary.main' : 'grey.100', color: isCurrent ? '#fff' : 'text.secondary',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{v.version}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={600} fontSize={13.5}>{v.note}</Typography>
              <Typography variant="caption" color="text.disabled">{v.when}</Typography>
              <Box sx={{ mt: 0.5 }}><Chip size="small" label={`status: ${v.status}`} /></Box>
              {v.forkedFrom && <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>↳ forked from {forkedFromLabel(v.forkedFrom, tenants)}</Typography>}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
              {isCurrent ? <Chip size="small" color="success" label="Current" /> : (
                <>
                  <Button size="small" variant="outlined" onClick={() => onMakeActive(v.version)}>Make current</Button>
                  <Button size="small" variant="outlined" onClick={() => onEditVersion(v.version)}>Edit</Button>
                </>
              )}
            </Box>
          </Card>
        );
      })}
    </Box>
  );
}
